import { assertNever } from "@decibeltrade/utils";

import type { TransferMethod } from "@/lib/analytics";

// Type-only import: importing the runtime `isWithdrawTransfer` from the .tsx card
// would drag its icon imports into unit tests. Direction is derived locally instead.
import type { TransferProgressData } from "./transfer-progress-card";

/**
 * POST body for `/api/v1/transfers`. snake_case to match the Rust
 * RecordTransferRequestDto exactly — this object is JSON.stringify'd as-is.
 */
export interface TransferReportPayload {
  account: string;
  transfer_id: string;
  direction: "deposit" | "withdraw";
  method: TransferMethod;
  broker?: string;
  origin_chain?: string;
  wallet_provider?: string;
  amount: number;
  asset: string;
  tx_hash: string;
}

export interface TransferReportContext {
  /** Connected wallet name (e.g. "petra", "metamask"). */
  walletProvider?: string;
  /** Mesh broker name, only known for mesh deposits. */
  broker?: string;
}

function directionForType(type: TransferProgressData["type"]): "deposit" | "withdraw" {
  switch (type) {
    case "cctp-withdraw":
    case "queued-withdraw":
    case "queued-xchain-withdraw":
    case "xchain-withdraw":
      return "withdraw";
    case "cctp-deposit":
    case "mesh-deposit":
    case "xchain-deposit":
    case "fun-deposit":
      return "deposit";
    default:
      assertNever(type, "Unhandled transfer type in directionForType");
  }
}

function methodForType(type: TransferProgressData["type"]): TransferMethod {
  switch (type) {
    case "mesh-deposit":
      return "mesh";
    // NEAR Intents joins the CCTP cases here: it is a bridge from the warehouse's point of view,
    // and `method` is a fixed enum shared with the Rust RecordTransferRequestDto, so a dedicated
    // value would need a cross-service change. `origin_chain` already separates the two.
    case "cctp-deposit":
    case "cctp-withdraw":
    case "xchain-deposit":
    case "xchain-withdraw":
    case "fun-deposit":
      return "bridge";
    case "queued-withdraw":
    case "queued-xchain-withdraw":
      return "direct";
    default:
      assertNever(type, "Unhandled transfer type in methodForType");
  }
}

/**
 * The Decibel subaccount that joins to on-chain trading data: the destination
 * subaccount for deposits, the withdrawing subaccount for withdrawals. For
 * withdrawals this is deliberately NOT `accountToWithdrawTo` (the external
 * recipient) or `userAddress` (the owner wallet) — neither joins to trades.
 */
function accountForTransfer(transfer: TransferProgressData): string {
  switch (transfer.type) {
    case "cctp-deposit":
    case "mesh-deposit":
    case "xchain-deposit":
    case "fun-deposit":
      return transfer.subaccountToDepositTo;
    case "cctp-withdraw":
    case "queued-withdraw":
    case "queued-xchain-withdraw":
    case "xchain-withdraw":
      return transfer.subaccountAddress;
    default:
      assertNever(transfer, "Unhandled transfer type in accountForTransfer");
  }
}

/**
 * The Aptos-side on-chain tx hash for reconciliation (may be empty). For CCTP the
 * Aptos leg differs by direction: a deposit MINTS on Aptos (`destinationTxHash`),
 * a withdraw BURNS on Aptos (`sourceTxHash`). We deliberately report the Aptos
 * hash — never the external-chain hash (the other end of the bridge) — so `tx_hash`
 * can join to on-chain Aptos trading data.
 */
function txHashForTransfer(transfer: TransferProgressData): string {
  switch (transfer.type) {
    case "cctp-withdraw":
      // Aptos is the source (burn); `destinationTxHash` is the external claim.
      return transfer.sourceTxHash || transfer.txHash || "";
    case "cctp-deposit":
      // Aptos is the destination (mint/claim); `sourceTxHash` is the external burn.
      return transfer.destinationTxHash || transfer.txHash || "";
    case "xchain-deposit":
    case "fun-deposit":
      // 1Click settles on Aptos; the origin-chain hash is the user's own send and does not
      // join to Aptos trading data.
      return transfer.destinationTxHash || transfer.txHash || "";
    case "mesh-deposit":
    case "queued-withdraw":
    case "queued-xchain-withdraw":
      // Single Aptos-native tx.
      return transfer.txHash || transfer.destinationTxHash || "";
    case "xchain-withdraw":
      // The Aptos leg is our own send into the 1Click deposit address. What 1Click does past
      // that settles on another chain entirely and does not join to Aptos trading data.
      return transfer.txHash || "";
    default:
      assertNever(transfer, "Unhandled transfer type in txHashForTransfer");
  }
}

/**
 * The ticker the warehouse files this transfer under.
 *
 * Every flow but the two Intents ones is USDC-only by construction. A cross-chain withdrawal is
 * the whole point of BTC and APT support, and a cross-chain deposit lands whatever asset its
 * collector was configured for — so pinning "USDC" here would file a bitcoin-denominated amount
 * in the USDC column and silently corrupt per-asset volume. The fallbacks are for cards persisted
 * before the symbol was carried; each of those was a USDC route.
 */
function assetForTransfer(transfer: TransferProgressData): string {
  switch (transfer.type) {
    case "xchain-withdraw":
      return transfer.symbol || "USDC";
    case "xchain-deposit":
    case "fun-deposit":
      return transfer.destinationSymbol || "USDC";
    case "cctp-deposit":
    case "mesh-deposit":
    case "cctp-withdraw":
    case "queued-withdraw":
    case "queued-xchain-withdraw":
      return "USDC";
    default:
      assertNever(transfer, "Unhandled transfer type in assetForTransfer");
  }
}

/**
 * Build the normalized attribution record for a confirmed transfer. Pure — the
 * caller is responsible for the network POST and the analytics event.
 */
export function buildTransferReport(
  transfer: TransferProgressData,
  ctx: TransferReportContext = {},
): TransferReportPayload {
  return {
    account: accountForTransfer(transfer),
    transfer_id: transfer.id,
    direction: directionForType(transfer.type),
    method: methodForType(transfer.type),
    broker: ctx.broker,
    origin_chain: transfer.sourceChain,
    wallet_provider: ctx.walletProvider,
    amount: transfer.amount,
    asset: assetForTransfer(transfer),
    tx_hash: txHashForTransfer(transfer),
  };
}
